/* The red squares — a turn somebody was passed over for — and taking one off.
 *
 * A red square is charged two ways (store.js): the rotation reaching somebody
 * the desk has bypassed — Break, Other, or a time block Mangomint has them on —
 * and handing the turn to the next person (`chargeSkips`); or somebody pressing
 * *Skip this turn* for them (`skipOneTurn`). Either way it costs them a full
 * turn's credit, which is what keeps a bypass from coming back as a licence to
 * take the next four turns.
 *
 * The square itself is stored as the column position it landed at (`skips`, on
 * the day row), which is all the rotation needs. Why it was charged is kept
 * beside it, in the same order (`skipWhy`), so that the desk can be told before
 * it takes one off (Kevin, 2026-10-25: right-click a red square to remove it,
 * with a sheet saying why it was charged). A square charged before that was
 * kept has no record, and says so.
 *
 * Pure; imports nothing.
 *
 *   record   { at, kind, reason, label, takerName, serviceName }
 *            kind    'passed' — the rotation went past them to `takerName`
 *                    'pass'   — *Skip this turn* was pressed for them
 *            reason  for 'passed', why they were bypassed: 'break', 'other',
 *                    or 'block' for a Mangomint time block; `label` is its
 *                    name as the desk saw it ("Break", "Lunch")
 */

/* Which entry of `skips` the n-th red square down a column is. The column
 * draws them in position order, and two at the same position in the order they
 * were charged, so this is a stable sort of the indices. -1 for none. */
export function skipIndexForOrdinal(skips, ordinal) {
  var list = (skips || []).map(function (at, i) { return { at: at, i: i }; });
  list.sort(function (a, b) { return a.at - b.at || a.i - b.i; });
  var hit = list[ordinal];
  return hit ? hit.i : -1;
}

/* Both arrays without entry `index`. `why` may be shorter than `skips` — the
 * squares charged before reasons were kept — and comes back no longer than
 * `skips`. New arrays; the ones given are left alone. */
export function withoutSkip(skips, why, index) {
  var s = (skips || []).slice();
  var w = (why || []).slice(0, s.length);
  if (index < 0 || index >= s.length) return { skips: s, why: w };
  s.splice(index, 1);
  if (index < w.length) w.splice(index, 1);
  return { skips: s, why: w };
}

/* `why` with `record` set at `index`, padded with nulls for the squares before
 * it that have none, so the two arrays stay in step. */
export function withWhy(why, index, record) {
  var w = (why || []).slice();
  while (w.length < index) w.push(null);
  w[index] = record || null;
  return w;
}

/* The sheet's explanation, as sentences. `clock` formats a time. */
export function skipStory(record, staffName, clock) {
  var who = staffName || 'They';
  if (!record) {
    return 'The board did not record why this one was charged — it was ' +
      'marked before reasons were kept.';
  }
  var when = record.at != null && clock ? ' at ' + clock(record.at) : '';
  if (record.kind === 'pass') {
    return who + ' passed on this turn' + when + ' — somebody pressed Skip ' +
      'this turn for them.';
  }
  var why = record.reason === 'block'
    ? 'on a time block in Mangomint' + (record.label ? ' (' + record.label + ')' : '')
    : record.reason === 'break' ? 'on a Break'
    : 'being skipped' + (record.label && record.label !== 'Other' ? ' (' + record.label + ')' : '');
  var to = record.takerName
    ? ' and the rotation went past them to ' + record.takerName +
      (record.serviceName ? ' for a ' + record.serviceName : '')
    : ' and the rotation went past them';
  return who + ' was ' + why + when + to + '.';
}
